import { i18n } from '../../../app/i18n';
import { addMonths, epochDayOf, plainDateFromEpochDay } from '../../../core/services/plain-date';

import type { Span } from '../types';

export type Duration = { years: number; months: number; days: number };

/** Inclusive of both end days: Aug 25 -> Aug 26 is 2 days. */
export const totalDaysOf = (span: Span): number => span.endDay - span.startDay + 1;

/**
 * Calendar-accurate breakdown: months are consumed one real calendar month at a time from the
 * start date, so February counts as a month and a 30-day August span does not.
 */
export const durationOf = (span: Span): Duration => {
  // Nothing in Phase 0 can produce a reversed span (`spanOf` normalises), but `BarState.previewSpan`
  // is a hand-built `Span` from Phase 2 onward — guard rather than trust every future caller.
  if (span.endDay < span.startDay) return { years: 0, months: 0, days: 0 };

  const start = plainDateFromEpochDay(span.startDay);
  // The span covers endDay itself, so measure to the day after it.
  const exclusiveEnd = span.endDay + 1;

  let months = 0;
  while (epochDayOf(addMonths(start, months + 1)) <= exclusiveEnd) {
    months += 1;
  }

  const days = exclusiveEnd - epochDayOf(addMonths(start, months));

  return { years: Math.floor(months / 12), months: months % 12, days };
};

/**
 * Task 23 fix 1. English-only sibling of `formatDuration`, for the ONE caller that splices a
 * duration into an author-supplied English sentence: the Gantt bar's `aria-label`
 * (`view/bar.tsx`). `formatDuration` IS localised (`common:day`), while the rest of that sentence
 * — the `MMM dd` dates from date-fns and the `, ` / ` to ` glue — is not and cannot be without a
 * date-fns locale registry this app does not have (nothing in `apps/webapp/src` imports
 * `date-fns/locale`). Mixing the two produced one accessible name in two languages, announced
 * under one `lang`, which is worse for a French screen-reader user than a name that is uniformly
 * English. So the accessible name is uniformly English until a real i18n pass can localise the
 * whole template; see the Task 23 fix-1 report §5.
 *
 * Latent rather than live today: `locales/fr/common.json` has no `day`/`month`/`year` keys, so
 * `formatDuration` already falls back to `en` in French. One added translation key turns the
 * splice on, with nothing to connect the regression back to here — which is exactly why this is
 * fixed now rather than left as an inherited gap.
 *
 * Deliberately NOT `formatDuration`-with-a-`lng`-option: `i18n.t(key, { lng: 'en' })` reaches into
 * the shared i18next instance's fallback machinery for a string that must never move, and would
 * silently start returning French the day someone changes `fallbackLng`. Fifteen lines of literals
 * cannot.
 */
export const formatDurationEnglish = ({ years, months, days }: Duration): string => {
  const plural = (count: number, unit: string): string =>
    `${count} ${unit}${count === 1 ? '' : 's'}`;
  const parts: string[] = [];

  if (years > 0) parts.push(plural(years, 'year'));
  if (months > 0) parts.push(plural(months, 'month'));
  if (days > 0) parts.push(plural(days, 'day'));

  if (parts.length === 0) return plural(0, 'day');
  if (parts.length === 1) return parts[0];

  const last = parts.pop();

  return `${parts.join(', ')} and ${last}`;
};

export const formatDuration = ({ years, months, days }: Duration): string => {
  const t = i18n.t;
  const parts: string[] = [];

  if (years > 0) parts.push(t('common:year', { count: years }));
  if (months > 0) parts.push(t('common:month', { count: months }));
  if (days > 0) parts.push(t('common:day', { count: days }));

  if (parts.length === 0) return t('common:day', { count: 0 });
  if (parts.length === 1) return parts[0];

  const last = parts.pop();

  return `${parts.join(', ')} and ${last}`;
};
